import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { configureCKEditorUpload } from './ckEditorUploadAdapter';
import { downloadDocx } from '../utils/downloadDocx';

// Lazy load DecoupledEditor to avoid conflicts with ClassicEditor
let DecoupledEditor = null;
const loadDecoupledEditor = async () => {
  if (!DecoupledEditor) {
    const module = await import('@ckeditor/ckeditor5-build-decoupled-document');
    DecoupledEditor = module.default;
  }
  return DecoupledEditor;
};

// Get full CKEditor config - same as LearningProgressModal
const ckEditorFullConfig = {
  toolbar: {
    items: [
      'undo',
      'redo',
      '|',
      'findAndReplace',
      'selectAll',
      '|',
      'heading',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'subscript',
      'superscript',
      'code',
      'removeFormat',
      '|',
      'specialCharacters',
      'horizontalLine',
      'pageBreak',
      'link',
      'insertImage',
      'insertImageViaUrl',
      'insertTable',
      'highlight',
      'blockQuote',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
      'todoList',
      'indent',
      'outdent',
      '|',
      'accessibilityHelp'
    ],
    shouldNotGroupWhenFull: false
  },
  fontFamily: {
    supportAllValues: true
  },
  fontSize: {
    options: [10, 12, 14, 'default', 18, 20, 22],
    supportAllValues: true
  },
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
      { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
      { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
      { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
    ]
  },
  image: {
    toolbar: [
      'toggleImageCaption',
      'imageTextAlternative',
      '|',
      'imageStyle:inline',
      'imageStyle:wrapText',
      'imageStyle:breakText',
      '|',
      'resizeImage'
    ]
  },
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: 'https://',
    decorators: {
      toggleDownloadable: {
        mode: 'manual',
        label: 'Downloadable',
        attributes: {
          download: 'file'
        }
      }
    }
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true
    }
  },
  menuBar: {
    isVisible: true
  },
  placeholder: 'Type or paste your content here!',
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
  }
};

export default function SavedContentModal({ 
  open, 
  onClose, 
  title,
  content,
  runId,
  qualificationId,
  subtaskCode,
  onSave,
  programmeName = 'LEARNING PROGRAMME',
  moduleName = 'Module Content',
  referencesPendingCompletion = false,
  referencesDesignerNotice = null,
  wordCount = 0,
  pageCount = 0,
  changeLog = []
}) {
  const [editorInstance, setEditorInstance] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [isEditorLayoutReady, setIsEditorLayoutReady] = useState(false);
  const [decoupledEditorLoaded, setDecoupledEditorLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [docxGenerating, setDocxGenerating] = useState(false);
  const [exportError, setExportError] = useState(null);
  const editorContainerRef = useRef(null);
  const editorMenuBarRef = useRef(null);
  const editorToolbarRef = useRef(null);
  const lastSavedContentRef = useRef('');
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setIsEditorLayoutReady(true);
    loadDecoupledEditor().then(() => {
      setDecoupledEditorLoaded(true);
    });
    return () => setIsEditorLayoutReady(false);
  }, []);

  // Update content when prop changes; treat as last-saved when loading
  useEffect(() => {
    if (content && editorInstance) {
      editorInstance.setData(content);
      setEditorContent(content);
      lastSavedContentRef.current = content;
      setIsDirty(false);
    } else if (content) {
      setEditorContent(content);
      lastSavedContentRef.current = content;
      setIsDirty(false);
    }
  }, [content, editorInstance]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      setSaveError(null);
      
      // Get content from CKEditor
      const contentToSave = editorInstance ? editorInstance.getData() : editorContent;
      
      if (!contentToSave || contentToSave.trim().length === 0) {
        setSaveError('No content available to save');
        setSaving(false);
        return;
      }

      const hasViewInEditorPath = subtaskCode && qualificationId;
      const hasRunPath = runId && qualificationId;
      if (!hasViewInEditorPath && !hasRunPath) {
        setSaveError(subtaskCode ? 'Qualification ID is required to save' : (runId ? 'Qualification ID is required to save' : 'Run ID or subtask code is required to save'));
        setSaving(false);
        return;
      }

      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let saveResponse;
      if (hasViewInEditorPath) {
        // Use clean document title (strip "Saved Content: " so it isn't stored as page heading)
        const documentTitle = (title || 'Saved Learning Document').replace(/^Saved Content:\s*/i, '').trim() || 'Saved Learning Document';
        saveResponse = await fetch(`/api/qualifications/${qualificationId}/learning-content/save-build-content`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            subtask_code: subtaskCode,
            html_content: contentToSave,
            title: documentTitle
          })
        });
      } else {
        saveResponse = await fetch('/api/learning/save-document', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            run_id: runId,
            html_content: contentToSave,
            title: title || 'Saved Learning Document',
            qualification_id: qualificationId
          })
        });
      }

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save: ${saveResponse.statusText}`);
      }

      const result = await saveResponse.json();

      lastSavedContentRef.current = contentToSave;
      setIsDirty(false);
      setSaveSuccess(true);
      setSaveError(null);
      
      if (onSave) {
        onSave(result);
      }
      
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {

      setSaveError(error.message || 'Failed to save document');
      setSaveSuccess(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadWord = async () => {
    const fromEditor = editorInstance ? editorInstance.getData() : '';
    const contentToExport = (fromEditor && fromEditor.trim()) ? fromEditor : (editorContent || content || '');
    if (!contentToExport || contentToExport.trim().length === 0) {
      setExportError('No content available to download');
      return;
    }
    try {
      setDocxGenerating(true);
      setExportError(null);
      await downloadDocx({
        programmeName: programmeName || 'LEARNING PROGRAMME',
        moduleName: moduleName || 'Module Content',
        contentHtml: contentToExport
      });
    } catch (error) {
      setExportError(error.message || 'Failed to download Word document');
    } finally {
      setDocxGenerating(false);
    }
  };

  const hasExportContent = Boolean(
    (editorContent || content || (editorInstance ? editorInstance.getData() : '') || '').trim()
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography component="span" variant="h6">
            {title || 'Saved Content'}
          </Typography>
          <IconButton onClick={onClose} size="small" type="button">
            <CloseIcon />
          </IconButton>
        </Stack>
        {(wordCount > 0 || pageCount > 0) && (
          <Typography variant="caption" color="text.secondary" component="div">
            {wordCount > 0 ? `${Number(wordCount).toLocaleString()} words` : ''}
            {wordCount > 0 && pageCount > 0 ? ' · ' : ''}
            {pageCount > 0 ? `${pageCount} page${pageCount === 1 ? '' : 's'}` : ''}
          </Typography>
        )}
        {runId && (
          <Typography variant="caption" color="text.secondary" component="div">
            Run ID: {runId}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {Array.isArray(changeLog) && changeLog.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Document change log
            </Typography>
            {changeLog.slice(-8).reverse().map((entry, idx) => (
              <Typography key={`change-${idx}`} variant="caption" component="div">
                {entry.ts ? new Date(entry.ts).toLocaleString() : ''}
                {entry.ts ? ' — ' : ''}
                {String(entry.action || 'change').replace(/_/g, ' ')}
                {entry.word_count != null ? ` (${Number(entry.word_count).toLocaleString()} words)` : ''}
              </Typography>
            ))}
          </Alert>
        )}
        {referencesPendingCompletion && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" component="span" sx={{ fontWeight: 600 }}>
              References need designer completion
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
              {referencesDesignerNotice ||
                'Verify every reference against real sources. Replace placeholders and expand retrieval URLs into full citations where needed. Do not invent authors, dates, publishers, or URLs.'}
            </Typography>
          </Alert>
        )}
        <Box
          className="learner-reading-host"
          sx={{ minHeight: 500, maxHeight: 'calc(90vh - 200px)', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: 'grey.50' }}
        >
          <Box sx={{ 
            '& .editor-container': {
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            },
            '& .editor-container__menu-bar': {
              borderBottom: '1px solid #ddd',
              minHeight: '40px'
            },
            '& .editor-container__toolbar': {
              borderBottom: '1px solid #ddd',
              minHeight: '40px'
            },
            '& .editor-container__editor-wrapper': {
              flex: 1,
              overflow: 'auto'
            },
            '& .editor-container__editor': {
              minHeight: '400px'
            }
          }}>
            <div className="editor-container editor-container_document-editor" ref={editorContainerRef}>
              <div className="editor-container__menu-bar" ref={editorMenuBarRef}></div>
              <div className="editor-container__toolbar" ref={editorToolbarRef}></div>
              <div className="editor-container__editor-wrapper">
                <div className="editor-container__editor">
                  {isEditorLayoutReady && decoupledEditorLoaded && DecoupledEditor ? (
                    <CKEditor
                      editor={DecoupledEditor}
                      data={editorContent || ''}
                      config={ckEditorFullConfig}
                      onReady={(editor) => {
                        configureCKEditorUpload(editor);
                        setEditorInstance(editor);

                        // Attach toolbar and menu bar for DecoupledEditor
                        if (editorToolbarRef.current) {
                          editorToolbarRef.current.appendChild(editor.ui.view.toolbar.element);
                        }
                        if (editorMenuBarRef.current) {
                          editorMenuBarRef.current.appendChild(editor.ui.view.menuBarView.element);
                        }
                        
                        // Set initial content if available
                        if (editorContent) {
                          editor.setData(editorContent);
                        }
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        setEditorContent(data);
                        if (data !== lastSavedContentRef.current) {
                          setIsDirty(true);
                        }
                      }}
                      onAfterDestroy={() => {
                        // Clean up toolbar and menu bar
                        if (editorToolbarRef.current) {
                          Array.from(editorToolbarRef.current.children).forEach(child => child.remove());
                        }
                        if (editorMenuBarRef.current) {
                          Array.from(editorMenuBarRef.current.children).forEach(child => child.remove());
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                      <Typography sx={{ ml: 2 }}>Loading editor...</Typography>
                    </Box>
                  )}
                </div>
              </div>
            </div>
          </Box>
        </Box>
        
        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
        {exportError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {exportError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          type="button"
          onClick={handleDownloadWord}
          variant="outlined"
          startIcon={docxGenerating ? <CircularProgress size={16} /> : <DescriptionIcon />}
          disabled={docxGenerating || !hasExportContent}
        >
          {docxGenerating ? 'Downloading Word...' : 'Download Word'}
        </Button>
        {(runId && qualificationId) || (subtaskCode && qualificationId) ? (
          <Button 
            type="button"
            onClick={handleSave}
            variant="contained" 
            startIcon={saving ? <CircularProgress size={16} /> : (saveSuccess ? <CheckCircleIcon /> : <SaveIcon />)}
            color={saveSuccess ? "success" : "primary"}
            disabled={saving}
            sx={{ 
              ...(saveSuccess && { 
                bgcolor: 'success.main',
                '&:hover': { bgcolor: 'success.dark' }
              })
            }}
          >
            {saving ? 'Saving...' : saveSuccess ? 'Saved!' : (isDirty ? 'Save' : 'Save Changes')}
          </Button>
        ) : null}
        <Button 
          type="button"
          onClick={onClose} 
          variant="outlined"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}


